import React, { useEffect, useRef, useState } from 'react';
import './DoorCodePrompt.scss';

const PANEL_WIDTH = 340;

interface ShowEventDetail {
  refrId: number;
}

// Player-facing (any player, not just admins) prompt shown when someone
// activates a locked door/container that has a code fallback configured but
// doesn't qualify via faction/person. Submitting just unlocks the reference
// server-side for a short window (see FactionZoneSystem.CODE_UNLOCK_WINDOW_MS)
// - the player still has to press E again themselves to actually open it,
// same as any other unlocked-but-untouched object.
const DoorCodePrompt: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [refrId, setRefrId] = useState<number | null>(null);
  const [code, setCode] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as ShowEventDetail;
      setRefrId(detail.refrId);
      setCode('');
      setVisible(true);
    };
    window.addEventListener('skymp5-client:promptDoorCode', handler);
    return () => window.removeEventListener('skymp5-client:promptDoorCode', handler);
  }, []);

  useEffect(() => {
    if (visible) inputRef.current?.focus();
  }, [visible]);

  const send = (message: string) => {
    if ((window as any).skyrimPlatform?.sendMessage) {
      (window as any).skyrimPlatform.sendMessage(message);
    }
  };

  const close = () => setVisible(false);

  const submit = () => {
    if (refrId === null || !code) return;
    send('door-code-submit:' + JSON.stringify({ refrId, code }));
    close();
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') submit();
    if (e.key === 'Escape') close();
  };

  if (!visible) return null;

  return (
    <div className="door-code-overlay">
      <div className="door-code-panel skyrim-panel" style={{ width: `${PANEL_WIDTH}px` }}>
        <div className="door-code-header">
          <div className="door-code-header-title">Code d'accès</div>
        </div>
        <input
          ref={inputRef}
          className="door-code-input"
          type="text"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Entrer le code..."
        />
        <div className="door-code-actions">
          <button type="button" className="door-code-btn door-code-btn--ghost" onClick={close}>
            Annuler
          </button>
          <button type="button" className="door-code-btn door-code-btn--primary" onClick={submit}>
            Valider
          </button>
        </div>
      </div>
    </div>
  );
};

export default DoorCodePrompt;
